
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { InteractiveService } from '../../services/interactive/InteractiveService';
import { motion, AnimatePresence } from 'framer-motion';
// Note: Assuming framer-motion is installed as it's common in modern react apps. 
// If not, I should use standard CSS transitions. 
// Checked package.json earlier, didn't explicitly see it but 'ui' chunk had it?
// Let's stick to standard CSS Tailwind classes to be safe and fast.

const StudentClient = () => {
    const { code: urlCode } = useParams();

    const [step, setStep] = useState<'join' | 'waiting' | 'answering'>('join');
    const [name, setName] = useState('');
    const [code, setCode] = useState(urlCode || '');
    const [ws, setWs] = useState<WebSocket | null>(null);
    const [question, setQuestion] = useState<any>(null);
    const [submitted, setSubmitted] = useState<any>(null);
    const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'error'>('connecting');

    const handleJoin = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!name || !code) return;

        setConnectionStatus('connecting');

        // Check if code valid
        const isValid = await InteractiveService.checkSession(code);
        if (!isValid) {
            alert('Mã phiên không hợp lệ!');
            setConnectionStatus('error');
            return;
        }

        const socket = InteractiveService.connectStudent(code, name, (newQuestion) => {
            if (newQuestion) {
                setQuestion(newQuestion);
                setStep('answering');
                setSubmitted(null);
            } else {
                setStep('waiting');
                setQuestion(null);
            }
        });

        socket.onopen = () => setConnectionStatus('connected');
        socket.onerror = () => {
            alert('Lỗi kết nối!');
            setConnectionStatus('error');
        };

        setWs(socket);
        setStep('waiting');
    };

    const submitAnswer = (answer: any) => {
        if (!ws) return;
        ws.send(JSON.stringify({
            action: 'SUBMIT_ANSWER',
            payload: { answer }
        }));
        setSubmitted(answer);
    };

    // --- Render Screens ---

    if (step === 'join') {
        return (
            <div className="min-h-screen bg-gradient-to-br from-indigo-600 to-purple-700 flex flex-col items-center justify-center p-6 text-white font-sans">
                <div className="w-full max-w-sm bg-white/10 backdrop-blur-md border border-white/20 p-8 rounded-3xl shadow-2xl animate-in zoom-in duration-500">
                    <div className="text-center mb-8">
                        <div className="w-16 h-16 bg-white rounded-2xl mx-auto flex items-center justify-center text-3xl shadow-lg mb-4">
                            🎓
                        </div>
                        <h1 className="text-2xl font-bold tracking-tight">Tham gia lớp học</h1>
                        <p className="text-white/60 text-sm mt-1">Vật Lí 365 Interactive</p>
                    </div>

                    <form onSubmit={handleJoin} className="flex flex-col gap-5">
                        <div className="relative group">
                            <label className="text-xs font-bold uppercase tracking-wider text-indigo-200 mb-1 block group-focus-within:text-white transition-colors">Mã PIN</label>
                            <input
                                value={code}
                                onChange={(e) => setCode(e.target.value)}
                                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-center text-3xl font-mono tracking-[0.2em] font-bold outline-none focus:bg-white/10 focus:border-white/30 transition-all placeholder-white/20 text-white"
                                placeholder="000000"
                                maxLength={6}
                                inputMode="numeric"
                            />
                        </div>

                        <div className="relative group">
                            <label className="text-xs font-bold uppercase tracking-wider text-indigo-200 mb-1 block group-focus-within:text-white transition-colors">Họ và Tên</label>
                            <input
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-lg font-medium outline-none focus:bg-white/10 focus:border-white/30 transition-all placeholder-white/20 text-white"
                                placeholder="Nhập tên của bạn..."
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={!name || !code}
                            className={`mt-4 py-4 rounded-xl font-bold text-lg shadow-lg transition-all transform active:scale-95 flex items-center justify-center gap-2
                                ${name && code
                                    ? 'bg-white text-indigo-600 hover:bg-indigo-50 hover:shadow-xl'
                                    : 'bg-white/10 text-white/30 cursor-not-allowed'
                                }`}
                        >
                            Vào lớp ngay 🚀
                        </button>
                    </form>
                </div>
                <div className="mt-8 text-xs text-white/30 font-medium tracking-widest uppercase">
                    Powered by VatLi365
                </div>
            </div>
        );
    }

    if (step === 'waiting') {
        return (
            <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6 text-center">
                <div className="w-full max-w-md bg-white p-8 rounded-3xl shadow-xl border border-slate-100 animate-pulse">
                    <div className="w-20 h-20 bg-indigo-50 rounded-full flex items-center justify-center mx-auto mb-6 text-indigo-500">
                        <svg className="w-10 h-10 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                    </div>
                    <h2 className="text-2xl font-bold text-slate-800 mb-2">Xin chào, {name}!</h2>
                    <p className="text-slate-500">Bạn đã vào lớp thành công.</p>
                    <div className="my-6 py-3 px-6 bg-slate-100 rounded-xl inline-block font-mono font-bold text-slate-600 tracking-widest text-xl">
                        {code}
                    </div>
                    <p className="text-sm font-medium text-slate-400 uppercase tracking-wide">
                        Vui lòng hướng mắt lên màn hình giáo viên
                    </p>
                </div>
            </div>
        );
    }

    // Answering Step
    return (
        <div className="min-h-screen bg-slate-100 flex flex-col font-sans">
            {/* Header */}
            <header className="bg-white px-6 py-4 shadow-sm border-b sticky top-0 z-10">
                <div className="max-w-xl mx-auto flex justify-between items-center">
                    <div className="font-bold text-slate-700">{name}</div>
                    <div className="text-xs font-mono bg-slate-100 px-2 py-1 rounded text-slate-500">{code}</div>
                </div>
            </header>

            <main className="flex-1 flex flex-col max-w-xl mx-auto w-full p-6">
                {/* Question Info */}
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 mb-6">
                    <span className="inline-block px-3 py-1 bg-indigo-50 text-indigo-600 text-xs font-bold rounded-lg uppercase tracking-wider mb-3">
                        {question?.type === 'mcq' ? 'Trắc nghiệm' : question?.type === 'word_cloud' ? 'Word Cloud' : 'Trả lời ngắn'}
                    </span>
                    <h2 className="text-xl font-bold text-slate-800 leading-relaxed">
                        {question?.data?.text}
                    </h2>
                </div>

                {/* Answer Area */}
                <div className="flex-1">
                    {submitted ? (
                        <div className="h-full flex flex-col items-center justify-center p-8 text-center animate-in zoom-in duration-300">
                            <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mb-6 text-green-500 shadow-sm">
                                <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <h3 className="text-2xl font-bold text-slate-800 mb-2">Đã gửi câu trả lời!</h3>

                            {question.type === 'mcq' && (
                                <div className="mt-4 p-4 bg-white rounded-xl border border-slate-200 shadow-sm w-full">
                                    <p className="text-sm text-slate-400 mb-1 uppercase tracking-wide font-bold">Lựa chọn của bạn</p>
                                    <p className="text-3xl font-black text-indigo-600">{submitted}</p>
                                </div>
                            )}

                            {(question.type === 'short_answer' || question.type === 'word_cloud') && (
                                <div className="mt-4 p-4 bg-white rounded-xl border border-slate-200 shadow-sm w-full">
                                    <p className="text-sm text-slate-400 mb-1 uppercase tracking-wide font-bold">Bạn đã nhập</p>
                                    <p className="text-lg font-medium text-slate-800 italic">"{submitted}"</p>
                                </div>
                            )}

                            <p className="mt-8 text-slate-400 animate-pulse">Đang đợi câu hỏi tiếp theo...</p>
                        </div>
                    ) : (
                        <div className="animate-in slide-in-from-bottom-4 duration-500">
                            {question?.type === 'mcq' && (
                                <div className="grid grid-cols-1 gap-3">
                                    {(question.data.options || ['A', 'B', 'C', 'D']).map((opt: string, idx: number) => {
                                        // Colors for options
                                        const colors = [
                                            'border-l-blue-500',
                                            'border-l-green-500',
                                            'border-l-yellow-500',
                                            'border-l-red-500'
                                        ];
                                        const colorClass = colors[idx % colors.length] || 'border-l-indigo-500';

                                        return (
                                            <button
                                                key={opt}
                                                onClick={() => submitAnswer(opt)}
                                                className={`w-full p-5 bg-white border border-slate-200 border-l-8 ${colorClass} rounded-xl shadow-sm hover:shadow-md hover:-translate-y-1 transition-all text-left group`}
                                            >
                                                <div className="flex items-center gap-4">
                                                    <span className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center font-bold text-slate-500 group-hover:bg-indigo-100 group-hover:text-indigo-600 transition-colors">
                                                        {String.fromCharCode(65 + idx)}
                                                    </span>
                                                    <span className="text-lg font-medium text-slate-700 group-hover:text-slate-900">{opt}</span>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}

                            {(question?.type === 'word_cloud' || question?.type === 'short_answer') && (
                                <div>
                                    <textarea
                                        className="w-full p-6 bg-white border-2 border-indigo-100 rounded-2xl focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 outline-none text-xl min-h-[200px] shadow-sm mb-4 placeholder-slate-300 transition-all text-slate-700"
                                        placeholder="Nhập câu trả lời của bạn tại đây..."
                                        autoFocus
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && !e.shiftKey) {
                                                e.preventDefault();
                                                const val = e.currentTarget.value.trim();
                                                if (val) submitAnswer(val);
                                            }
                                        }}
                                    />
                                    <button
                                        onClick={(e) => {
                                            const textarea = e.currentTarget.previousSibling as HTMLTextAreaElement;
                                            const val = textarea.value.trim();
                                            if (val) submitAnswer(val);
                                        }}
                                        className="w-full py-4 bg-indigo-600 text-white rounded-xl font-bold text-xl shadow-lg hover:bg-indigo-700 active:scale-95 transition-all flex items-center justify-center gap-2"
                                    >
                                        Gửi đi 🚀
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};

export default StudentClient;
